import { useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { ApiError, setUnauthorizedHandler, type Me } from "./api/client";
import { keys, useLogout, useMe, useToReview } from "./api/queries";
import { useProjectEvents } from "./api/realtime";
import { ErrorBox, Loading, usePageTitle } from "./components/ui";
import { CommandPalette, openPalette } from "./components/palette";
import { LoginPage } from "./pages/LoginPage";
import { useScope } from "./pages/scope";

// Pages chargées à la demande (PB-108) : la connexion n'embarque ni le markdown, ni yaml, ni les pages du projet.
const AccountPage = lazy(() => import("./pages/AccountPage").then((m) => ({ default: m.AccountPage })));
const BoardPage = lazy(() => import("./pages/BoardPage").then((m) => ({ default: m.BoardPage })));
const HealthPage = lazy(() => import("./pages/HealthPage").then((m) => ({ default: m.HealthPage })));
const KnowledgePage = lazy(() => import("./pages/KnowledgePage").then((m) => ({ default: m.KnowledgePage })));
const LivePage = lazy(() => import("./pages/LivePage").then((m) => ({ default: m.LivePage })));
const MembersPage = lazy(() => import("./pages/MembersPage").then((m) => ({ default: m.MembersPage })));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage").then((m) => ({ default: m.OnboardingPage })));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage").then((m) => ({ default: m.ProjectsPage })));
const OrgPage = lazy(() => import("./pages/OrgPage").then((m) => ({ default: m.OrgPage })));
const ReviewPage = lazy(() => import("./pages/ReviewPage").then((m) => ({ default: m.ReviewPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const TodayPage = lazy(() => import("./pages/TodayPage").then((m) => ({ default: m.TodayPage })));

/** Page d'arrivée d'un projet : Aujourd'hui, sauf pour le brain d'organisation, qui n'a que de la connaissance (PB-112, PB-116). */
function ProjectIndex() {
  return <Navigate to={useScope().org ? "knowledge" : "today"} replace />;
}

export function App() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const location = useLocation();

  useEffect(() => {
    setUnauthorizedHandler(() => {
      client.removeQueries({ queryKey: keys.me });
      if (!location.pathname.startsWith("/login")) navigate(`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`);
    });
  }, [client, navigate, location.pathname, location.search]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<Authenticated />}>
        <Route index element={<ProjectsPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="org" element={<OrgPage />} />
        <Route path="p/:project" element={<ProjectLayout />}>
          <Route index element={<ProjectIndex />} />
          <Route path="today" element={<TodayPage />} />
          <Route path="board" element={<BoardPage />} />
          <Route path="live" element={<LivePage />} />
          <Route path="knowledge/:section?" element={<KnowledgePage />} />
          <Route path="knowledge/item/:key" element={<KnowledgePage />} />
          <Route path="review" element={<ReviewPage />} />
          <Route path="health" element={<HealthPage />} />
          <Route path="onboarding" element={<OnboardingPage />} />
          <Route path="members" element={<MembersPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Que faire de la session (PB-103) : seul un vrai 401 renvoie à la connexion. Un 5xx ou une coupure réseau pendant un
 * rechargement garde les données déjà là (TanStack passe pourtant en erreur) ; sans données, on affiche l'erreur.
 */
export function sessionGate(me: { isPending: boolean; isError: boolean; error: unknown; data: unknown }): "loading" | "login" | "error" | "ok" {
  if (me.error instanceof ApiError && me.error.status === 401) return "login";
  if (me.data) return "ok";
  if (me.isPending) return "loading";
  return me.isError ? "error" : "loading";
}

/**
 * Page courante, pour le focus : /p/WF/knowledge/item/DEC-1 et /p/WF/knowledge/decisions sont la même page (on y
 * choisit un élément), /p/WF/board et /p/WF/review non.
 */
export function pageOf(pathname: string): string {
  return pathname.split("/").filter(Boolean).slice(0, 3).join("/");
}

/** Après une navigation, le focus passe au contenu : le lecteur d'écran annonce la nouvelle page (PB-108). */
function useFocusOnNavigation() {
  const page = pageOf(useLocation().pathname);
  const previous = useRef(page);
  useEffect(() => {
    if (previous.current === page) return;
    previous.current = page;
    document.getElementById("main")?.focus();
  }, [page]);
}

/** Garde : sans session, retour à la connexion ; sinon barre latérale + page (PB-116). */
function Authenticated() {
  const me = useMe();
  const location = useLocation();
  useFocusOnNavigation();
  const gate = sessionGate(me);
  if (gate === "loading") return <Loading />;
  if (gate === "login") return <Navigate to={`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (gate === "error" || !me.data)
    return (
      <div className="center">
        <div className="card stack" style={{ gap: 12, maxWidth: 480 }}>
          <ErrorBox error={me.error} />
          <button className="primary" onClick={() => void me.refetch()}>
            Réessayer
          </button>
        </div>
      </div>
    );
  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Aller au contenu
      </a>
      <Sidebar me={me.data} />
      <div className="content">
        <Suspense fallback={<Loading />}>
          <Outlet context={me.data} />
        </Suspense>
      </div>
    </div>
  );
}

/** « À revoir » de la barre latérale : le même compte que la page et qu'Aujourd'hui (PB-120). */
function ReviewLink({ tenant, project, fallback }: { tenant: string; project: string; fallback: number }) {
  const count = useToReview({ tenant, project });
  const total = count.total || fallback;
  return (
    <NavLink to={`/p/${project}/review`} className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>
      À revoir
      {total ? <span className="nav-count warn">{total}</span> : null}
    </NavLink>
  );
}

/**
 * Navigation principale (PB-116) : sélecteur de projet, puis les sections du projet groupées par usage — Travail,
 * Mémoire, Équipe — au lieu d'onglets à plat ; le brain d'organisation et le compte en pied.
 */
function Sidebar({ me }: { me: Me }) {
  const { project } = useParams();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const current = me.projects.find((p) => p.key === project);
  // Sur téléphone, les sections se replient derrière « Menu » ; elles se referment à chaque navigation.
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [location.pathname]);
  const link = (to: string, label: string, count?: number, tone?: "warn" | "ok") => (
    <NavLink to={`/p/${current!.key}/${to}`} className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>
      {label}
      {count ? <span className={`nav-count ${tone ?? ""}`}>{count}</span> : null}
    </NavLink>
  );
  return (
    <nav className="sidebar" aria-label="Navigation principale">
      <Link to="/" className="brand">
        <img src="/favicon.svg" alt="" />
        ProjectBrain
      </Link>
      <button className="nav-toggle" aria-expanded={open} aria-controls="nav-sections" onClick={() => setOpen(!open)}>
        Menu
      </button>
      <div id="nav-sections" className={`nav-sections${open ? " open" : ""}`}>
      {current && (
        <ProjectSelector me={me} current={current.key} onChange={(key) => navigate(switchProject(location.pathname, current.key, key))} />
      )}
      {current && <MemorySearch project={current.key} />}
      {current && (
        <button className="nav-command" onClick={openPalette} title="Aller à une page, ouvrir une tâche ou une fiche, lancer une action">
          Aller à… <kbd>Ctrl K</kbd>
        </button>
      )}
      {current && !current.org && (
        <>
          <div className="nav-group">{link("today", "Aujourd'hui")}</div>
          <div className="nav-group">
            <div className="nav-title">Travail</div>
            {link("board", "Tâches", current.open_work)}
            {link("live", "En direct", current.live_sessions, "ok")}
          </div>
        </>
      )}
      {current && (
        <>
          <div className="nav-group">
            <div className="nav-title">Mémoire</div>
            {link("knowledge", "Connaissance")}
            <ReviewLink tenant={me.tenant} project={current.key} fallback={current.observations} />
            {link("health", "Santé et usage")}
          </div>
          <div className="nav-group">
            <div className="nav-title">Équipe</div>
            {link("members", "Membres")}
            {!current.org && link("onboarding", "Onboarding")}
            {!current.org && link("settings", "Réglages")}
          </div>
        </>
      )}
      {!current && (
        <div className="nav-group">
          <div className="nav-title">Projets</div>
          {me.projects.map((p) => (
            <NavLink key={p.key} to={`/p/${p.key}`} className="nav-link">
              {p.name}
              <span className="nav-count">{p.key}</span>
            </NavLink>
          ))}
        </div>
      )}
      <div className="nav-foot">
        {/* Toujours là (PB-126) : ce que l'organisation partage, même avant que son brain existe. */}
        <NavLink to="/org" className={({ isActive }) => `nav-link${isActive || current?.org ? " active" : ""}`}>
          Organisation · mémoire commune
        </NavLink>
        <div className="nav-user">
          <Link to="/account" title="Mes appareils et credentials">
            {me.user}
          </Link>
          {current && <span>· {current.role}</span>}
          <span className="spacer" />
          <button className="ghost small" onClick={() => logout.mutate(undefined, { onSuccess: () => navigate("/login") })}>
            Déconnexion
          </button>
        </div>
      </div>
      </div>
    </nav>
  );
}

/**
 * Recherche dans la mémoire depuis n'importe quelle page (PB-116) : Entrée ouvre les résultats dans Connaissance ; la touche
 * « / » y place le curseur, hors d'un champ de saisie.
 */
function MemorySearch({ project }: { project: string }) {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || target.isContentEditable) return;
      e.preventDefault();
      input.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        const q = input.current?.value.trim();
        if (q) navigate(`/p/${project}/knowledge?q=${encodeURIComponent(q)}`);
      }}
    >
      <input ref={input} type="search" className="nav-search" aria-label="Rechercher dans la mémoire" placeholder="Rechercher…   /" />
    </form>
  );
}

/** Même section dans l'autre projet : /p/WF/review → /p/OPS/review (une fiche de connaissance revient à la liste). */
export function switchProject(pathname: string, from: string, to: string): string {
  const prefix = `/p/${from}/`;
  const rest = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : "";
  const section = rest.split("/")[0];
  return section ? `/p/${to}/${section}` : `/p/${to}`;
}

/** Sélecteur de projet (PB-73) : un nom simple s'il n'y a qu'un projet, sinon une liste accessible au clavier. */
export function ProjectSelector({ me, current, onChange }: { me: Me; current: string; onChange: (key: string) => void }) {
  const project = me.projects.find((p) => p.key === current);
  if (me.projects.length < 2) return <strong style={{ padding: "0 12px" }}>{project?.name ?? current}</strong>;
  return (
    <select aria-label="Projet" value={current} onChange={(e) => onChange(e.target.value)}>
      {me.projects.map((p) => (
        <option key={p.key} value={p.key}>
          {p.org ? "Organisation" : p.name} ({p.key})
        </option>
      ))}
    </select>
  );
}

const SECTION_TITLES: Record<string, string> = {
  today: "Aujourd'hui",
  board: "Tâches",
  live: "En direct",
  knowledge: "Connaissance",
  review: "À revoir",
  health: "Santé et usage",
  onboarding: "Onboarding",
  members: "Membres",
  settings: "Réglages du projet",
};

function ProjectLayout() {
  const { project = "" } = useParams();
  const me = useMe().data!;
  const summary = me.projects.find((p) => p.key === project);
  const live = useProjectEvents(me.tenant, project);
  const section = useLocation().pathname.split("/")[3] ?? "";
  const title = SECTION_TITLES[section];
  usePageTitle(title, summary?.name ?? project);
  if (!summary) return <div className="page"><ErrorBox error={new Error(`Projet ${project} introuvable ou inaccessible.`)} /></div>;

  return (
    <main className="page" id="main" tabIndex={-1}>
      <div className="page-head page-title">
        <div>
          <p className="crumb row" style={{ margin: 0 }}>
            <span>{me.tenant}</span>
            <span>/</span>
            <span>{summary.org ? "Organisation" : summary.name}</span>
            <span className="key">{summary.key}</span>
            <span>·</span>
            <span className="row" title="Mises à jour en temps réel (SignalR)">
              <span className={`dot ${live === "live" ? "ok" : live === "connecting" ? "warn" : "danger"}`} />
              {live === "live" ? "temps réel" : live === "connecting" ? "connexion…" : "hors ligne"}
            </span>
          </p>
          <h1>{title ?? summary.name}</h1>
        </div>
        {summary.org && (
          <p className="small muted" style={{ maxWidth: 420, margin: 0 }}>
            Brain d'organisation : ce que les administrateurs acceptent ici est hérité par tous les projets, sous la forme <code>org:CLÉ</code>.
          </p>
        )}
      </div>
      <Suspense fallback={<Loading />}>
        <Outlet context={{ tenant: me.tenant, project, role: summary.role, donePolicy: summary.done_policy, org: summary.org ?? false }} />
      </Suspense>
      <CommandPalette tenant={me.tenant} project={project} org={summary.org ?? false} />
    </main>
  );
}
